import React, {memo, useCallback, useMemo, useRef, useState} from 'react';
import {Card, Collapse, Dialog, DialogBody, Drawer, DrawerSize, H3, H4, H5, H6, Icon, Popover} from '@blueprintjs/core';
import styled from 'styled-components';

import {validate, getByPath, resolveFieldSchema, deepMergeSchema} from './validate';
import {TextField, SelectField, MultiSelectField, SwitchField, TextAreaField, JsonTextAreaField, ArrayField, CascadingSelectField, DatePickerField, DynamicObjectField} from './fields';
import {EditableTitle, EditableDescription} from './fields/item-card';
import {sfVars, GroupHeaderExtraContext, CollectionGroupContext, ErrorTag} from './fields/shared';


// --- Utilities ---

const parseScopePath = (scope) => {
    if (!scope || scope === '#') return [];
    return scope.replace('#/properties/', '').split('/properties/');
};

const setByPath = (obj, path, value) => {
    if (path.length === 0) return value;
    const result = Array.isArray(obj) ? [...obj] : {...obj};
    const [head, ...rest] = path;
    result[head] = rest.length === 0 ? value : setByPath(result[head] ?? {}, rest, value);
    return result;
};

const labelFromKey = (key) =>
    key.replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase()).trim();


// --- Theme root — CSS custom properties with defaults ---

const FormRoot = styled.div`
    ${sfVars}
    --sf-max-width: none;
    --sf-width: 100%;
    width: var(--sf-width);
    max-width: var(--sf-max-width);
`;


// --- Styled layout wrappers ---

const VerticalLayoutWrapper = styled.div`
    display: flex;
    flex-direction: column;
    gap: var(--sf-vertical-gap);
`;

const HorizontalLayoutWrapper = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: var(--sf-horizontal-gap);
    flex-wrap: ${p => p.$wrap === false ? 'nowrap' : 'wrap'};
    & > * { flex: ${p => p.$wrap === false ? '1' : '1 1 auto'}; }
`;

const GroupCard = styled(Card)`
    margin: var(--sf-group-margin) 0;
    padding: var(--sf-group-padding);
    border-radius: var(--sf-border-radius);
    background: var(--sf-group-background) !important;

    &[class*="bp"][class*="-card"] {
        box-shadow: 0 0 0 1px rgba(17, 20, 24, 0.1),
                    0 1px 1px rgba(17, 20, 24, 0.1),
                    0 2px 6px rgba(17, 20, 24, 0.1);
    }
`;

const GroupLabel = styled(H5)`
    color: var(--sf-group-label-color);
    font-size: var(--sf-group-label-size);
`;

const HEADINGS = [H3, H4, H5, H6];
const headingFor = (depth) => HEADINGS[Math.min(depth || 0, HEADINGS.length - 1)];

const GroupHeader = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
    cursor: pointer;
    user-select: none;
`;

const CollapseIcon = styled(Icon)`
    color: #5c7080;
`;

const collectControlPaths = (element) => {
    if (!element) return [];
    if (element.type === 'Control') {
        const path = parseScopePath(element.scope);
        return [path.length ? '/' + path.join('/') : ''];
    }
    if (element.elements) return element.elements.flatMap(collectControlPaths);
    return [];
};

const collectGroupErrors = (element, errors) => {
    const paths = collectControlPaths(element);
    return errors.filter(e => {
        const ip = e.instancePath || '';
        return paths.some(p => ip === p || ip.startsWith(p + '/'));
    });
};


const PortalContent = styled.div`${sfVars}`;

const PANEL_WIDTH = 'min(1080px, 90vw)';
const PANEL_MODES = {popover: true, overlay: true, drawer: true};

const EditModeGroup = ({element, depth, errors, readonly, children}) => {
    const editMode = element.options?.editMode || (element.options?.collapsed != null ? 'collapsible' : undefined);
    const [collapsed, setCollapsed] = useState(!!element.options?.collapsed);
    const [panelOpen, setPanelOpen] = useState(false);
    const [headerExtra, setHeaderExtra] = useState(null);
    const headerExtraKeyRef = React.useRef(null);
    const stableSetHeaderExtra = React.useCallback((key, tags) => {
        if (key !== headerExtraKeyRef.current) {
            headerExtraKeyRef.current = key;
            setHeaderExtra(tags);
        }
    }, []);
    const contentHidden = editMode === 'collapsible' ? collapsed : !!PANEL_MODES[editMode];
    const groupErrors = contentHidden ? collectGroupErrors(element, errors) : [];
    const expandGroup = () => {
        if (editMode === 'collapsible') setCollapsed(false);
        else if (PANEL_MODES[editMode]) setPanelOpen(true);
    };

    const content = (
        <GroupHeaderExtraContext.Provider value={stableSetHeaderExtra}>
            <VerticalLayoutWrapper style={{marginTop: 8}}>
                {children}
            </VerticalLayoutWrapper>
        </GroupHeaderExtraContext.Provider>
    );

    // Default (no editMode): always expanded, no interactivity
    if (!editMode) {
        return (
            <GroupCard>
                <GroupHeader as="div" style={{cursor: 'default'}}>
                    <GroupLabel as={headingFor(depth)} style={{margin: 0}}>{element.label}</GroupLabel>
                    {headerExtra}
                </GroupHeader>
                {content}
            </GroupCard>
        );
    }

    // Collapsible: current caret toggle behavior
    if (editMode === 'collapsible') {
        return (
            <GroupCard>
                <GroupHeader as="div" style={{cursor: 'default'}}>
                    <CollapseIcon icon={collapsed ? 'caret-right' : 'caret-down'} onClick={() => setCollapsed(c => !c)} />
                    <GroupLabel as={headingFor(depth)} style={{margin: 0}}>{element.label}</GroupLabel>
                    <ErrorTag errors={groupErrors} onClick={expandGroup} />
                    {headerExtra}
                </GroupHeader>
                <Collapse isOpen={!collapsed} keepChildrenMounted>
                    {content}
                </Collapse>
            </GroupCard>
        );
    }

    // Popover: edit icon, hover opens popover with content
    const pw = element.options?.panelWidth || PANEL_WIDTH;
    if (editMode === 'popover') {
        return (
            <GroupCard>
                <GroupHeader as="div" style={{cursor: 'default'}}>
                    <Popover
                        interactionKind="hover"
                        placement="right-start"
                        content={<PortalContent style={{padding: 12, width: pw, maxHeight: 400, overflow: 'auto'}}>
                            <GroupLabel as={headingFor(depth)} style={{margin: '0 0 8px'}}>{element.label}</GroupLabel>
                            {content}
                        </PortalContent>}
                        hoverOpenDelay={200}
                        hoverCloseDelay={100}
                    >
                        <Icon icon={readonly ? 'eye-open' : 'edit'} style={{color: '#5c7080', cursor: 'pointer'}} />
                    </Popover>
                    <GroupLabel as={headingFor(depth)} style={{margin: 0}}>{element.label}</GroupLabel>
                    <ErrorTag errors={groupErrors} onClick={expandGroup} />
                    {headerExtra}
                </GroupHeader>
            </GroupCard>
        );
    }

    // Overlay / Drawer: edit icon, click opens Dialog or Drawer
    const Panel = editMode === 'drawer' ? (
        <Drawer isOpen={panelOpen} onClose={() => setPanelOpen(false)} size={pw} title={element.label}>
            <PortalContent style={{padding: 20}}>{content}</PortalContent>
        </Drawer>
    ) : (
        <Dialog isOpen={panelOpen} onClose={() => setPanelOpen(false)} title={element.label} style={{width: pw}}>
            <DialogBody><PortalContent>{content}</PortalContent></DialogBody>
        </Dialog>
    );

    return (
        <GroupCard>
            <GroupHeader as="div" style={{cursor: 'default'}}>
                <Icon icon={readonly ? 'eye-open' : 'edit'} style={{color: '#5c7080', cursor: 'pointer'}} onClick={() => setPanelOpen(true)} />
                <GroupLabel as={headingFor(depth)} style={{margin: 0}}>{element.label}</GroupLabel>
                <ErrorTag errors={groupErrors} onClick={expandGroup} />
                {headerExtra}
            </GroupHeader>
            {Panel}
        </GroupCard>
    );
};


const CollectionGroup = ({label, title, description, depth, editMode: editModeProp, initialCollapsed, readonly, errors: groupErrors, panelWidth, children}) => {
    const editMode = editModeProp || (initialCollapsed != null ? 'collapsible' : undefined);
    const [collapsed, setCollapsed] = useState(!!initialCollapsed);
    const [panelOpen, setPanelOpen] = useState(false);
    const [headerExtra, setHeaderExtra] = useState(null);
    const headerExtraKeyRef = useRef(null);
    const stableSetHeaderExtra = useCallback((key, tags) => {
        if (key !== headerExtraKeyRef.current) {
            headerExtraKeyRef.current = key;
            setHeaderExtra(tags);
        }
    }, []);

    const expand = useCallback(() => {
        if (editMode === 'collapsible') setCollapsed(false);
        else if (PANEL_MODES[editMode]) setPanelOpen(true);
    }, [editMode]);
    const collectionCtx = useMemo(() => ({setHeaderExtra: stableSetHeaderExtra, expand}), [stableSetHeaderExtra, expand]);

    const contentHidden = editMode === 'collapsible' ? collapsed : !!PANEL_MODES[editMode];
    const visibleErrors = contentHidden ? (groupErrors || []) : [];

    const inner = (
        <CollectionGroupContext.Provider value={collectionCtx}>
            <GroupHeaderExtraContext.Provider value={null}>
                {children}
            </GroupHeaderExtraContext.Provider>
        </CollectionGroupContext.Provider>
    );

    // Default (no editMode): card with title/label + headerExtra + description + children
    if (!editMode) {
        return (
            <GroupCard>
                <GroupHeader as="div" style={{cursor: 'default'}}>
                    {title || (label && <GroupLabel as={headingFor(depth)} style={{margin: 0}}>{label}</GroupLabel>)}
                    {headerExtra}
                </GroupHeader>
                {description}
                {inner}
            </GroupCard>
        );
    }

    // Collapsible: caret toggle
    if (editMode === 'collapsible') {
        return (
            <GroupCard>
                <GroupHeader as="div" style={{cursor: 'default'}}>
                    <CollapseIcon icon={collapsed ? 'caret-right' : 'caret-down'} onClick={() => setCollapsed(c => !c)} />
                    {title || (label && <GroupLabel as={headingFor(depth)} style={{margin: 0}}>{label}</GroupLabel>)}
                    <ErrorTag errors={visibleErrors} onClick={expand} />
                    {headerExtra}
                </GroupHeader>
                {description}
                <Collapse isOpen={!collapsed} keepChildrenMounted>{inner}</Collapse>
            </GroupCard>
        );
    }

    // Popover: hover opens popover with title + description + headerExtra + children
    const pw = panelWidth || PANEL_WIDTH;
    if (editMode === 'popover') {
        return (
            <GroupCard>
                <GroupHeader as="div" style={{cursor: 'default'}}>
                    <Popover
                        interactionKind="hover"
                        placement="right-start"
                        content={<PortalContent style={{padding: 12, width: pw, maxHeight: 400, overflow: 'auto'}}>
                            {title || (label && <GroupLabel as={headingFor(depth)} style={{margin: '0 0 8px'}}>{label}</GroupLabel>)}
                            {description}
                            {headerExtra}
                            {inner}
                        </PortalContent>}
                        hoverOpenDelay={200}
                        hoverCloseDelay={100}
                    >
                        <Icon icon={readonly ? 'eye-open' : 'edit'} style={{color: '#5c7080', cursor: 'pointer'}} />
                    </Popover>
                    {label && <GroupLabel as={headingFor(depth)} style={{margin: 0}}>{label}</GroupLabel>}
                    <ErrorTag errors={visibleErrors} onClick={expand} />
                </GroupHeader>
            </GroupCard>
        );
    }

    // Drawer / Overlay: click opens panel
    const panelTitle = (
        <span style={{display: 'flex', alignItems: 'center', gap: 6}}>
            {title || label}
            {headerExtra}
            <ErrorTag errors={groupErrors || []} />
        </span>
    );
    const Panel = editMode === 'drawer' ? (
        <Drawer isOpen={panelOpen} onClose={() => setPanelOpen(false)} size={pw} title={panelTitle}>
            <PortalContent style={{padding: 20}}>
                {description}
                {inner}
            </PortalContent>
        </Drawer>
    ) : (
        <Dialog isOpen={panelOpen} onClose={() => setPanelOpen(false)} title={panelTitle} style={{width: pw}}>
            <DialogBody>
                <PortalContent>
                    {description}
                    {inner}
                </PortalContent>
            </DialogBody>
        </Dialog>
    );

    return (
        <GroupCard>
            <GroupHeader as="div" style={{cursor: 'default'}}>
                <Icon icon={readonly ? 'eye-open' : 'edit'} style={{color: '#5c7080', cursor: 'pointer'}} onClick={() => setPanelOpen(true)} />
                {label && <GroupLabel as={headingFor(depth)} style={{margin: 0}}>{label}</GroupLabel>}
                <ErrorTag errors={visibleErrors} onClick={expand} />
            </GroupHeader>
            {Panel}
        </GroupCard>
    );
};


// --- Schema → field resolution ---

const isType = (schema, t) => schema.type === t || (Array.isArray(schema.type) && schema.type.includes(t));

const resolveField = (fieldSchema, options) => {
    // options.renderer takes priority — lets consumers inject custom field types
    if (options?.renderer) return options.renderer;
    if (options?.levels) return 'cascading-select';

    if (isType(fieldSchema, 'boolean') || options?.toggle) return 'switch';

    // Object: nested:false opt-out → JSON textarea; otherwise auto-nest
    if (isType(fieldSchema, 'object') && options?.nested === false) return 'json-textarea';
    if (isType(fieldSchema, 'object') && options?.multi) return 'json-textarea';  // backward compat
    if (isType(fieldSchema, 'object') && (fieldSchema.properties || fieldSchema.additionalProperties)) return 'object';

    if (options?.multi) return 'textarea';
    if (options?.multiselect) return 'multiselect';

    // Array: objects → array-of-objects; primitives → multiselect
    if (isType(fieldSchema, 'array') && fieldSchema.items?.type === 'object' && fieldSchema.items?.properties) return 'array-of-objects';
    if (isType(fieldSchema, 'array')) return 'multiselect';

    if (fieldSchema.format === 'date-time') return 'date-picker';

    if (fieldSchema.enum) return 'select';
    if (fieldSchema.oneOf) return 'select';
    if (fieldSchema.examples) return 'select';
    return 'text';
};

const buildFieldProps = (fieldSchema, value, label, disabled, error, options, required) => {
    const displayLabel = required ? `${label} (required)` : label;
    const base = {label: displayLabel, value, disabled, error, icon: options?.icon};

    switch (resolveField(fieldSchema, options)) {
        case 'switch':
            return {field: 'switch', props: base};

        case 'textarea':
            return {field: 'textarea', props: {...base, rows: options?.rows}};

        case 'json-textarea':
            return {field: 'json-textarea', props: {...base, rows: options?.rows, schema: fieldSchema}};

        case 'multiselect': {
            const itemSchema = fieldSchema.items || fieldSchema;
            let msOptions;
            let allowCreate = false;
            if (itemSchema.examples) {
                msOptions = itemSchema.examples.map(v => ({label: String(v), value: v}));
                allowCreate = true;
            } else if (itemSchema.enum) {
                msOptions = itemSchema.enum.map(v => ({label: String(v), value: v}));
            } else if (itemSchema.oneOf) {
                msOptions = itemSchema.oneOf
                    .filter(o => o.const !== '')
                    .map(o => ({label: o.title || String(o.const), value: o.const}));
            } else {
                msOptions = [];
                allowCreate = true;
            }
            const msNullable = Array.isArray(fieldSchema.type) && fieldSchema.type.includes('null');
            return {field: 'multiselect', props: {...base, rawLabel: label, options: msOptions, allowCreate, nullable: msNullable, placeholder: options?.placeholder, fill: options?.fill, matchTargetWidth: options?.matchTargetWidth, showMax: options?.showMax}};
        }

        case 'date-picker':
            return {field: 'date-picker', props: {...base, time: options?.time, fill: options?.fill}};

        case 'select': {
            let selectOptions;
            let allowCreate = false;
            if (fieldSchema.examples) {
                selectOptions = fieldSchema.examples.map(v => ({label: String(v), value: v}));
                allowCreate = true;
            } else if (fieldSchema.enum) {
                selectOptions = fieldSchema.enum.map(v => ({label: String(v), value: v}));
            } else if (fieldSchema.oneOf) {
                selectOptions = fieldSchema.oneOf
                    .filter(o => o.const !== '')
                    .map(o => ({label: o.title || String(o.const), value: o.const}));
            }
            return {field: 'select', props: {...base, options: selectOptions, required, allowCreate, fill: options?.fill, matchTargetWidth: options?.matchTargetWidth}};
        }

        default: {
            const nullable = Array.isArray(fieldSchema.type) && fieldSchema.type.includes('null');
            const optional = !required || nullable;
            return {field: 'text', props: {...base, nullable, optional}};
        }
    }
};

// Built-in field registry
const builtinFields = {
    text: TextField,
    select: SelectField,
    multiselect: MultiSelectField,
    switch: SwitchField,
    textarea: TextAreaField,
    'json-textarea': JsonTextAreaField,
    'date-picker': DatePickerField,
};


// --- Auto-layout for nested objects ---

const generateAutoLayout = (objectSchema, horizontal) => ({
    type: horizontal ? 'HorizontalLayout' : 'VerticalLayout',
    elements: Object.keys(objectSchema.properties).map(key => ({
        type: 'Control',
        scope: '#/properties/' + key,
    })),
});

// Strip header fields (itemTitle, itemDescription) from a UI schema so they only render in the card header
const stripHeaderControls = (uischema, fields) => {
    if (!fields.length || !uischema) return uischema;
    const scopes = new Set(fields.map(f => '#/properties/' + f));
    if (uischema.type === 'Control') {
        return scopes.has(uischema.scope) ? null : uischema;
    }
    if (uischema.elements) {
        const filtered = uischema.elements
            .map(el => stripHeaderControls(el, fields))
            .filter(Boolean);
        return {...uischema, elements: filtered};
    }
    return uischema;
};


// --- Recursive element renderer ---

const RenderElement = ({element, schema, data, readonly, errors, renderers, depth = 0, onChange}) => {
    const recurse = (child, i) => (
        <RenderElement
            key={i}
            element={child}
            schema={schema}
            data={data}
            readonly={readonly}
            errors={errors}
            renderers={renderers}
            depth={depth}
            onChange={onChange}
        />
    );

    switch (element.type) {
        case 'VerticalLayout':
            return (
                <VerticalLayoutWrapper>
                    {element.elements.map(recurse)}
                </VerticalLayoutWrapper>
            );

        case 'HorizontalLayout':
            return (
                <HorizontalLayoutWrapper $wrap={element.options?.wrap}>
                    {element.elements.map(recurse)}
                </HorizontalLayoutWrapper>
            );

        case 'Group': {
            const hasResolvable = (els) => els.some(el => {
                if (el.type === 'Control') return !!resolveFieldSchema(schema, parseScopePath(el.scope), data);
                return el.elements ? hasResolvable(el.elements) : false;
            });
            if (!hasResolvable(element.elements)) {
                if (element.label) console.warn(`[SchemaForm] Group "${element.label}" hidden — no resolvable fields`);
                return null;
            }
            if (!element.label) {
                return (
                    <GroupCard>
                        <VerticalLayoutWrapper>
                            {element.elements.map(recurse)}
                        </VerticalLayoutWrapper>
                    </GroupCard>
                );
            }
            return (
                <EditModeGroup element={element} depth={depth} errors={errors} readonly={readonly}>
                    {element.elements.map(recurse)}
                </EditModeGroup>
            );
        }

        case 'Control': {
            const path = parseScopePath(element.scope);
            const fieldSchema = resolveFieldSchema(schema, path, data);
            if (!fieldSchema) return null;

            const value = getByPath(data, path);
            const fieldKey = path[path.length - 1];
            const label = element.label ?? (fieldKey ? labelFromKey(fieldKey) : '');
            const instancePath = path.length ? '/' + path.join('/') : '';
            const error = errors.find(e => e.instancePath === instancePath);

            // Check if field is required by its parent schema
            const parentPath = path.slice(0, -1);
            const parentSchema = parentPath.length === 0 ? schema : resolveFieldSchema(schema, parentPath, data);
            const required = (parentSchema?.required || []).includes(fieldKey);

            const disabled = readonly
                || fieldSchema.readOnly === true
                || element.options?.disabled === true
                || element.rule?.effect === 'DISABLE';

            const handleChange = (newValue) => {
                onChange(setByPath(data, path, newValue));
            };

            // Custom renderer — gets full context
            const rendererName = element.options?.renderer;
            if (rendererName && renderers?.[rendererName]) {
                const CustomRenderer = renderers[rendererName];
                return (
                    <CustomRenderer
                        label={label}
                        value={value}
                        disabled={disabled}
                        error={error}
                        required={required}
                        schema={fieldSchema}
                        data={data}
                        options={element.options}
                        onChange={handleChange}
                    />
                );
            }

            // Auto-nested object — generate a Group with Controls for each property
            const fieldType = resolveField(fieldSchema, element.options);
            if (fieldType === 'object') {
                const allowRenaming = element.options?.allowPropertyRenaming;
                const additionalPropsSchema = fieldSchema.additionalProperties;

                // Dynamic object: editable keys and/or additional properties
                if (allowRenaming || additionalPropsSchema) {
                    const horizontal = element.options?.horizontal;
                    const entrySchema = typeof additionalPropsSchema === 'object' ? additionalPropsSchema : null;

                    const getEntrySchema = (key) => {
                        let base = fieldSchema.properties?.[key] || entrySchema;
                        if (fieldSchema.dependentSchemas && value && typeof value === 'object') {
                            for (const [depKey, depSchema] of Object.entries(fieldSchema.dependentSchemas)) {
                                if (depKey in value && depSchema?.properties?.[key]) {
                                    base = deepMergeSchema(base, depSchema.properties[key]);
                                }
                            }
                        }
                        return base;
                    };

                    const entryErrors = (key) => errors
                        .filter(e => e.instancePath.startsWith(instancePath + '/' + key + '/'))
                        .map(e => ({...e, instancePath: e.instancePath.slice(instancePath.length + ('/' + key).length)}));

                    const titleKey = element.options?.itemTitle;
                    const titleVal = titleKey && value && typeof value === 'object' && !Array.isArray(value) && value[titleKey] !== undefined ? value[titleKey] : undefined;
                    const titlePropSchema = titleVal !== undefined ? fieldSchema.properties?.[titleKey] : null;
                    const collectionTitle = titleVal !== undefined ? (
                        <EditableTitle
                            value={titleVal}
                            disabled={disabled || !!titlePropSchema?.readOnly || !!element.options?.itemTitleReadOnly}
                            error={errors.find(e => e.instancePath === instancePath + '/' + titleKey)}
                            type={titlePropSchema?.type}
                            depth={depth}
                            placeholder={`(Edit ${titleKey})`}
                            onChange={(newVal) => handleChange({...value, [titleKey]: newVal})}
                        />
                    ) : undefined;

                    const descKey = element.options?.itemDescription;
                    const descVal = descKey && value && typeof value === 'object' && !Array.isArray(value) && value[descKey] !== undefined ? value[descKey] : undefined;
                    const descPropSchema = descVal !== undefined ? fieldSchema.properties?.[descKey] : null;
                    const collectionDesc = descVal !== undefined ? (
                        <EditableDescription
                            value={descVal}
                            disabled={disabled || !!descPropSchema?.readOnly || !!element.options?.itemDescriptionReadOnly}
                            error={errors.find(e => e.instancePath === instancePath + '/' + descKey)}
                            type={descPropSchema?.type}
                            depth={depth}
                            placeholder={`(Edit ${descKey})`}
                            onChange={(newVal) => handleChange({...value, [descKey]: newVal})}
                        />
                    ) : undefined;

                    const itemOpts = element.options?.itemUISchema?.options;

                    const subErrors = errors.filter(e => {
                        const ip = e.instancePath || '';
                        return ip === instancePath || ip.startsWith(instancePath + '/');
                    });

                    return (
                        <CollectionGroup label={label} depth={depth} title={collectionTitle} description={collectionDesc} editMode={element.options?.editMode} initialCollapsed={element.options?.collapsed} readonly={readonly} errors={subErrors} panelWidth={element.options?.panelWidth}>
                            <DynamicObjectField
                                value={value || {}}
                                disabled={disabled}
                                readonly={readonly}
                                reorderable={element.options?.reorderable !== false}
                                editMode={itemOpts?.editMode}
                                initialCollapsed={itemOpts?.collapsed}
                                entrySchema={entrySchema}
                                allowRenaming={!!allowRenaming}
                                propertyNames={fieldSchema.propertyNames?.enum}
                                fill={element.options?.fill}
                                depth={depth}
                                itemTitle={itemOpts?.itemTitle}
                                itemTitleReadOnly={itemOpts?.itemTitleReadOnly}
                                itemTitleType={entrySchema?.properties?.[itemOpts?.itemTitle]?.type}
                                itemDescription={itemOpts?.itemDescription}
                                itemDescriptionReadOnly={itemOpts?.itemDescriptionReadOnly}
                                itemDescriptionType={entrySchema?.properties?.[itemOpts?.itemDescription]?.type}
                                panelWidth={itemOpts?.panelWidth}
                                entryErrors={entryErrors}
                                error={error}
                                renderEntry={(entryValue, key, onEntryChange) => {
                                    const s = getEntrySchema(key);
                                    if (!s || !s.properties) return null;
                                    let layout = element.options?.itemUISchemas?.[key] || element.options?.itemUISchema || generateAutoLayout(s, horizontal);
                                    if (layout.type === 'Control' && !parseScopePath(layout.scope).length && s.properties) {
                                        layout = layout.options?.itemUISchema || generateAutoLayout(s, layout.options?.horizontal || horizontal);
                                    }
                                    const headerFields = [itemOpts?.itemTitle, itemOpts?.itemDescription].filter(Boolean);
                                    return (
                                        <RenderElement
                                            element={stripHeaderControls(layout, headerFields)}
                                            schema={s}
                                            data={entryValue || {}}
                                            readonly={readonly}
                                            errors={entryErrors(key)}
                                            renderers={renderers}
                                            depth={depth + 1}
                                            onChange={onEntryChange}
                                        />
                                    );
                                }}
                                onChange={handleChange}
                            />
                        </CollectionGroup>
                    );
                }

                const innerLayout = element.options?.itemUISchema || generateAutoLayout(fieldSchema, element.options?.horizontal);

                // Root-scope object: render layout directly, no collection group wrapper
                if (path.length === 0) {
                    return (
                        <RenderElement
                            element={innerLayout}
                            schema={schema}
                            data={data}
                            readonly={readonly}
                            errors={errors}
                            renderers={renderers}
                            depth={depth}
                            onChange={onChange}
                        />
                    );
                }

                const objTitleKey = element.options?.itemTitle;
                const objTitleVal = objTitleKey && value && typeof value === 'object' && !Array.isArray(value) && value[objTitleKey] !== undefined ? value[objTitleKey] : undefined;
                const objTitlePropSchema = objTitleVal !== undefined ? fieldSchema.properties?.[objTitleKey] : null;
                const objTitle = objTitleVal !== undefined ? (
                    <EditableTitle
                        value={objTitleVal}
                        disabled={disabled || !!objTitlePropSchema?.readOnly || !!element.options?.itemTitleReadOnly}
                        error={errors.find(e => e.instancePath === instancePath + '/' + objTitleKey)}
                        type={objTitlePropSchema?.type}
                        depth={depth}
                        placeholder={`(Edit ${objTitleKey})`}
                        onChange={(newVal) => handleChange({...value, [objTitleKey]: newVal})}
                    />
                ) : undefined;

                const objDescKey = element.options?.itemDescription;
                const objDescVal = objDescKey && value && typeof value === 'object' && !Array.isArray(value) && value[objDescKey] !== undefined ? value[objDescKey] : undefined;
                const objDescPropSchema = objDescVal !== undefined ? fieldSchema.properties?.[objDescKey] : null;
                const objDesc = objDescVal !== undefined ? (
                    <EditableDescription
                        value={objDescVal}
                        disabled={disabled || !!objDescPropSchema?.readOnly || !!element.options?.itemDescriptionReadOnly}
                        error={errors.find(e => e.instancePath === instancePath + '/' + objDescKey)}
                        type={objDescPropSchema?.type}
                        depth={depth}
                        placeholder={`(Edit ${objDescKey})`}
                        onChange={(newVal) => handleChange({...value, [objDescKey]: newVal})}
                    />
                ) : undefined;

                const headerFields = [objTitleKey, objDescKey].filter(Boolean);
                const subErrors = errors.filter(e => {
                    const ip = e.instancePath || '';
                    return ip === instancePath || ip.startsWith(instancePath + '/');
                });

                return (
                    <CollectionGroup label={label} depth={depth} title={objTitle} description={objDesc} editMode={element.options?.editMode} initialCollapsed={element.options?.collapsed} readonly={readonly} errors={subErrors} panelWidth={element.options?.panelWidth}>
                        <RenderElement
                            element={stripHeaderControls(innerLayout, headerFields)}
                            schema={schema}
                            data={data}
                            readonly={readonly}
                            errors={errors}
                            renderers={renderers}
                            depth={depth + 1}
                            onChange={onChange}
                        />
                    </CollectionGroup>
                );
            }

            // Array of objects — render items as cards with nested fields
            if (fieldType === 'array-of-objects') {
                const itemSchema = fieldSchema.items;
                const horizontal = element.options?.horizontal;
                const itemErrors = (index) => errors
                    .filter(e => e.instancePath.startsWith(instancePath + '/' + index + '/'))
                    .map(e => ({...e, instancePath: e.instancePath.slice(instancePath.length + ('/' + index).length)}));

                const arrItemOpts = element.options?.itemUISchema?.options;

                const subErrors = errors.filter(e => {
                    const ip = e.instancePath || '';
                    return ip === instancePath || ip.startsWith(instancePath + '/');
                });

                return (
                    <CollectionGroup label={label} depth={depth} editMode={element.options?.editMode} initialCollapsed={element.options?.collapsed} readonly={readonly} errors={subErrors} panelWidth={element.options?.panelWidth}>
                        <ArrayField
                            value={value || []}
                            nullable={Array.isArray(fieldSchema.type) && fieldSchema.type.includes('null')}
                            disabled={disabled}
                            readonly={readonly}
                            reorderable={element.options?.reorderable !== false}
                            horizontal={element.options?.horizontal}
                            editMode={arrItemOpts?.editMode}
                            initialCollapsed={arrItemOpts?.collapsed}
                            maxItems={fieldSchema.maxItems}
                            itemSchema={itemSchema}
                            depth={depth}
                            itemTitle={arrItemOpts?.itemTitle}
                            itemTitleReadOnly={arrItemOpts?.itemTitleReadOnly}
                            itemTitleType={itemSchema.properties?.[arrItemOpts?.itemTitle]?.type}
                            itemDescription={arrItemOpts?.itemDescription}
                            itemDescriptionReadOnly={arrItemOpts?.itemDescriptionReadOnly}
                            itemDescriptionType={itemSchema.properties?.[arrItemOpts?.itemDescription]?.type}
                            panelWidth={arrItemOpts?.panelWidth}
                            itemErrors={itemErrors}
                            error={error}
                            renderItem={(item, index, onItemChange) => {
                                let layout = element.options?.itemUISchema || generateAutoLayout(itemSchema, horizontal);
                                if (layout.type === 'Control' && !parseScopePath(layout.scope).length && itemSchema.properties) {
                                    layout = layout.options?.itemUISchema || generateAutoLayout(itemSchema, layout.options?.horizontal || horizontal);
                                }
                                const headerFields = [arrItemOpts?.itemTitle, arrItemOpts?.itemDescription].filter(Boolean);
                                return (<RenderElement
                                    element={stripHeaderControls(layout, headerFields)}
                                    schema={itemSchema}
                                    data={item || {}}
                                    readonly={readonly}
                                    errors={itemErrors(index)}
                                    renderers={renderers}
                                    depth={depth + 1}
                                    onChange={onItemChange}
                                />);
                            }}
                            onChange={handleChange}
                        />
                    </CollectionGroup>
                );
            }

            // Cascading select — multi-level dependent selector
            if (fieldType === 'cascading-select') {
                const subErrors = errors.filter(e =>
                    e.instancePath === instancePath || e.instancePath.startsWith(instancePath + '/')
                );
                return (
                    <CascadingSelectField
                        label={required ? `${label} (required)` : label}
                        value={value || {}}
                        disabled={disabled}
                        error={subErrors[0]}
                        levels={element.options.levels}
                        multi={element.options.multi}
                        onChange={handleChange}
                    />
                );
            }

            // Built-in field
            const {field, props} = buildFieldProps(fieldSchema, value, label, disabled, error, element.options, required);
            const FieldComponent = builtinFields[field];
            const fieldEl = <FieldComponent {...props} onChange={handleChange} />;

            const {minWidth, maxWidth} = element.options || {};
            if (minWidth || maxWidth) {
                return <div style={{minWidth, maxWidth}}>{fieldEl}</div>;
            }
            return fieldEl;
        }

        default:
            return null;
    }
};


// --- Debug overlay (REACT_APP_DEBUG_FORMS=true) ---

const DEBUG_FORMS = process.env.REACT_APP_DEBUG_FORMS === 'true';

const DebugOverlay = styled.div`
    margin-top: 8px;
    border: 1px dashed rgba(17, 20, 24, 0.3);
    border-radius: 6px;
    overflow: hidden;
`;

const DebugHeader = styled.div`
    padding: 4px 8px;
    font-size: 11px;
    font-weight: 600;
    color: #5c7080;
    background: rgba(17, 20, 24, 0.04);
    cursor: pointer;
    user-select: none;
`;

const DebugTextArea = styled.textarea`
    width: 100%;
    border: none;
    outline: none;
    resize: vertical;
    padding: 8px;
    font-family: monospace;
    font-size: 12px;
    line-height: 1.4;
    color: #394b59;
    background: rgba(17, 20, 24, 0.02);
    box-sizing: border-box;
`;


// --- Main component ---

const toErrorList = (errors) =>
    errors.map(e => ({[e.instancePath || '/']: e.message}));

const SchemaForm = memo(({schema, uischema, data, readonly = false, depth = 0, renderers, style, className, onChange}) => {
    const handleChange = (newData) => {
        if (!onChange) return;
        const errors = validate(schema, newData);
        onChange({data: newData, errors: toErrorList(errors)});
    };

    const errors = useMemo(() => validate(schema, data), [schema, data]);

    return (
        <FormRoot style={style} className={className}>
            <RenderElement
                element={uischema}
                schema={schema}
                data={data}
                readonly={readonly}
                errors={errors}
                renderers={renderers}
                depth={depth}
                onChange={handleChange}
            />
            {DEBUG_FORMS && (
                <DebugOverlay>
                    <DebugHeader>Form Data</DebugHeader>
                    <DebugTextArea
                        readOnly
                        rows={Math.min(JSON.stringify(data, null, 2).split('\n').length, 20)}
                        value={JSON.stringify(data, null, 2)}
                    />
                </DebugOverlay>
            )}
        </FormRoot>
    );
}, (prev, next) =>
    prev.schema === next.schema &&
    prev.uischema === next.uischema &&
    prev.data === next.data &&
    prev.readonly === next.readonly &&
    prev.depth === next.depth &&
    prev.renderers === next.renderers &&
    prev.style === next.style &&
    prev.className === next.className
);

export {SchemaForm};
